import { EVALUATION_ANCHORS, REQUIRED_LEVEL_ANCHOR_LABELS } from "@lib/constants/scoringScales";

import { GuideArticle, Section } from "../GuideShell";

const REQUIRED_LEVEL_ROWS = Array.from({ length: 10 }, (_, i) => i + 1).map((level) => ({
  level,
  label: REQUIRED_LEVEL_ANCHOR_LABELS[level],
}));

export function ScoringScalesPage() {
  return (
    <GuideArticle
      slug="scoring-scales"
      title="Scoring scales"
      intro={
        <p>
          Rarion's assessment model uses two scales: a 1–10 <em>required level</em> on the role
          blueprint and a 1–5 <em>evaluation</em> on each candidate. They serve different purposes —
          keeping them straight is the whole point of this page.
        </p>
      }
    >
      <Section id="why-dual-scale" title="Why dual-scale">
        <p>
          The 1–10 scale on each capability dimension defines the bar this role requires —
          calibrated to the seniority and scope of the position. The 1–5 scale on each assessment
          evaluates a specific candidate against that bar.
        </p>
        <p>
          The question for the assessor is not "is this person an 8?" — it's "given we need an 8 on
          this dimension, how well do they meet that bar?". Mixing the two scales is the single most
          common mistake in unstructured panel discussions.
        </p>
      </Section>

      <Section id="worked-example" title="Worked example">
        <p>
          A VP Engineering role calls for a <strong>required level 8</strong> on Leadership. A
          candidate is scored <strong>4 / 5</strong>.
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Right reading:</strong> "Strongly meets the 8-level Leadership bar this role
            requires."
          </li>
          <li>
            <strong>Wrong reading:</strong> "They are a 4 out of 10 on Leadership."
          </li>
        </ul>
        <p>
          The 1–5 number is always relative to the required level captured on the blueprint. Two
          candidates scoring 4 / 5 on dimensions with different required levels are not equivalent —
          one strongly meets a level-5 bar, the other strongly meets a level-9 bar. That is exactly
          the distinction the dual-scale model is designed to preserve.
        </p>
      </Section>

      <Section id="why-not-single-scale" title="Why this beats single-scale">
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong>Removes scale confusion.</strong> Reviewers are not asked to estimate absolute
            capability on a moving target.
          </li>
          <li>
            <strong>Improves panel alignment.</strong> Everyone scores against the same posted bar
            instead of their personal calibration.
          </li>
          <li>
            <strong>Defensible.</strong> The "why we hired" or "why we didn't" trail captures both
            the bar and the evaluation, not a single number with hidden assumptions.
          </li>
        </ul>
      </Section>

      <Section id="required-level-anchors" title="The 1–10 required-level anchors">
        <p>Used on the blueprint to define what this role requires for each dimension.</p>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-text-faint">
              <th className="py-2 pr-4">Level</th>
              <th className="py-2">Anchor</th>
            </tr>
          </thead>
          <tbody>
            {REQUIRED_LEVEL_ROWS.map((row) => (
              <tr key={row.level} className="border-b border-border last:border-b-0">
                <td className="py-1.5 pr-4 align-top font-medium text-text-primary">{row.level}</td>
                <td className="py-1.5 text-text-secondary">{row.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="evaluation-anchors" title="The 1–5 evaluation anchors">
        <p>Used in each candidate assessment to score how well the candidate meets the bar.</p>
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-text-faint">
              <th className="py-2 pr-4">Score</th>
              <th className="py-2 pr-4">Short</th>
              <th className="py-2">Anchor</th>
            </tr>
          </thead>
          <tbody>
            {EVALUATION_ANCHORS.map((anchor) => (
              <tr key={anchor.score} className="border-b border-border last:border-b-0">
                <td className="py-1.5 pr-4 align-top font-medium text-text-primary">
                  {anchor.score}
                </td>
                <td className="py-1.5 pr-4 align-top text-text-secondary">{anchor.short}</td>
                <td className="py-1.5 text-text-secondary">{anchor.label}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      <Section id="thresholds" title="Threshold rules">
        <p>
          Each blueprint can carry a threshold rule that turns the per-dimension scores into a
          non-blocking signal: minimum score on critical dimensions, minimum overall, and
          (optionally) a minimum number of dimensions where the candidate strongly meets the bar.
          The system never auto-decides hireability — failing the rule simply surfaces a "Below
          threshold" flag, and a recruiter can override with a logged reason. Surfaces (assessment
          form, summary panel, pipeline, shortlist report) flag the result so the cohort going into
          a panel discussion is honest about who is above or below the bar.
        </p>
      </Section>

      <Section id="six-dimensions" title="Why six dimensions, not more">
        <p>
          Blueprints cap at six capability dimensions. Above that, inter-rater reliability degrades
          quickly: assessors start trading off dimensions against each other instead of scoring them
          independently, and the per-dimension signal washes out. Five dimensions is the recommended
          target; the sixth is reserved for genuinely orthogonal requirements that cannot be folded
          into the others.
        </p>
      </Section>
    </GuideArticle>
  );
}
